
import { useEffect, useState } from 'react';
import { Lightbulb, Zap } from 'lucide-react';
import { type PowerCategory } from '@/lib/powers-data';
import { getCategoryCountFor } from '@/lib/powers-cms';
import { useCatalogPowers } from '@/components/painel/powers/powers-cms-context';
import { getCategoryEncyclopediaEntry, hasCategoryEncyclopediaEntry } from '@/lib/power-category-encyclopedia';
import { getPowerCategoryAccentHex } from '@/lib/power-category-theme';
import { GsImage, ORBITAL_ICON_BOX_STYLE } from '@/components/help-center/latest-power-shared';
import { cn } from '@/lib/utils';

const INFO_SMILEY_FETCH_PX = 40;

function CategoryInfoSmiley({ code, accentHex }: { code: string; accentHex: string }) {
  return (
    <div
      className="flex h-[72px] w-[72px] shrink-0 items-center justify-center rounded-2xl"
      style={{
        ...ORBITAL_ICON_BOX_STYLE,
        boxShadow: `0 0 0 1.5px ${accentHex}44, 0 0 0 2.5px #0f1e70, 6px 10px 22px rgba(0,0,0,0.45)`,
      }}
    >
      <GsImage
        code={code}
        sizePx={INFO_SMILEY_FETCH_PX}
        className="flex items-center justify-center"
        imgClassName="object-contain max-h-[52px] max-w-[52px]"
      />
    </div>
  );
}

interface PowersCategoryInfoProps {
  activeCategory: PowerCategory;
}

export default function PowersCategoryInfo({ activeCategory }: PowersCategoryInfoProps) {
  const [visible, setVisible] = useState(false);
  const { powers: catalogPowers } = useCatalogPowers();

  const entry = hasCategoryEncyclopediaEntry(activeCategory)
    ? getCategoryEncyclopediaEntry(activeCategory)
    : null;

  useEffect(() => {
    if (!entry) {
      setVisible(false);
      return;
    }
    setVisible(false);
    const timer = window.setTimeout(() => setVisible(true), 40);
    return () => window.clearTimeout(timer);
  }, [activeCategory, entry]);

  if (!entry) return null;

  const count = getCategoryCountFor(catalogPowers, activeCategory);
  const accentHex = getPowerCategoryAccentHex(activeCategory);
  const powerCountLabel =
    count === 1 ? '1 power nesta categoria' : `${count} powers nesta categoria`;

  return (
    <section className="border-t border-gray-100 bg-gray-50 px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <article
          className={cn(
            'relative overflow-hidden rounded-2xl border border-[#0f1e70] text-white',
            'bg-[#060c38] shadow-[0_20px_50px_rgba(0,0,0,0.28)]',
            'px-6 py-7 sm:px-8 sm:py-8 md:px-10 md:py-9',
            'transition-all duration-300 ease-out',
            visible ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
          )}
          aria-labelledby="powers-category-info-title"
        >
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'radial-gradient(ellipse at 18% 0%, #112268 0%, #0a1450 38%, #060c38 68%, #030719 100%)',
            }}
            aria-hidden
          />
          <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-sky-400/50 to-transparent" aria-hidden />

          <div className="relative flex flex-col gap-6 sm:flex-row sm:items-start sm:gap-8">
            <CategoryInfoSmiley code={entry.smileyCode} accentHex={accentHex} />

            <div className="min-w-0 flex-1">
              <h3
                id="powers-category-info-title"
                className="font-poppins text-xl font-bold tracking-tight sm:text-2xl"
                style={{ color: accentHex }}
              >
                {entry.label}
              </h3>

              <div className="mt-4 space-y-3">
                {entry.description.map((paragraph, index) => (
                  <p
                    key={index}
                    className="font-poppins text-sm leading-relaxed text-blue-100/75 sm:text-[15px]"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="mt-6 border-t border-white/[0.08] pt-5">
                <div className="grid grid-cols-[auto_1fr] items-start gap-x-2 gap-y-3">
                  <Zap
                    className="mt-0.5 h-4 w-4 shrink-0"
                    style={{ color: accentHex }}
                    aria-hidden
                  />
                  <p className="font-poppins text-sm font-semibold leading-snug text-white">
                    {powerCountLabel}
                  </p>

                  <Lightbulb
                    className="mt-0.5 h-4 w-4 shrink-0"
                    style={{ color: accentHex }}
                    aria-hidden
                  />
                  <p className="font-poppins text-sm leading-snug text-blue-100/65">
                    <span className="font-semibold text-blue-50/90">Curiosidade: </span>
                    {entry.curiosity}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </article>
      </div>
    </section>
  );
}
